import { http, HttpResponse, delay } from 'msw';
import {
  AutomationStatusEnum,
  CreateCategoryChampionRequest,
  EmployeeStatusEnum,
  RequestResponse as RequestDto,
  RequestStatusEnum,
  RequestTypeCategoryChampionV2,
  RequestTypeSettingDefaultsResponse as RequestTypeSettingDefaultsDto,
} from '@electricjs/core_entity-client';

import {
  requestTypeCategories,
  requestTypeCategoryChampionsV2,
  requestTypes,
  requests,
  systemRequesters,
} from '../data/requestData';
import { notionRequestTypeSettings } from '../data/requestTypeSettingsData';

// In-memory only, keyed by `applicationKey:requestTypeKey` -- reset on page
// reload, same as every other mock data store in this project.
const settingsDefaultsByAppAndRequestType = new Map<
  string,
  Record<string, string | string[] | null>
>();

// Minimal CSV body for the download endpoints (fetchAndDownloadFile reads it as a blob).
const requestsCsv = (rows: RequestDto[]) =>
  new HttpResponse(
    ['id,status', ...rows.map(r => `${r.id},${r.status}`)].join('\n'),
    { status: 200, headers: { 'Content-Type': 'text/csv' } }
  );

const resolveRequest = (requestId: string | readonly string[] | undefined) => {
  const index = requests.findIndex(r => r.id === requestId);
  if (index === -1) return undefined;
  requests[index] = {
    ...requests[index],
    status: RequestStatusEnum.Completed,
    resolved_at: new Date().toISOString(),
  };
  return requests[index];
};

const hasRequestTypeSlug = (request: (typeof requests)[number], slug: string) =>
  requestTypes.find(rt => rt.id === request.request_type_id)?.slug === slug;

export const requestsHandlers = [
  // Download routes must be registered BEFORE the `/requests/:request_id` handler.
  http.get(
    `*/v1/organizations/:organization_id/requests/me/download`,
    async () => {
      await delay(200);
      return requestsCsv(requests);
    }
  ),
  http.get(
    `*/v1/organizations/:organization_id/requests/download`,
    async () => {
      await delay(200);
      return requestsCsv(requests);
    }
  ),
  // Must be registered BEFORE the `/requests/:request_id` handler so 'me'
  // isn't matched as an id.
  http.get(
    `*/v1/organizations/:organization_id/requests/me`,
    async ({ request }) => {
      const url = new URL(request.url);
      const status = url.searchParams.get('status');
      const requestTypeSlug = url.searchParams.get('request_type_slug');

      const filtered = requests.filter(r => {
        if (status && r.status !== status) return false;
        if (requestTypeSlug && !hasRequestTypeSlug(r, requestTypeSlug)) {
          return false;
        }
        return true;
      });

      await delay(200);
      return HttpResponse.json(
        { results: filtered, total: filtered.length },
        { status: 200 }
      );
    }
  ),
  http.get(
    `*/v1/organizations/:organization_id/requests`,
    async ({ request }) => {
      const url = new URL(request.url);
      const params = url.searchParams;

      const assignedTo = params.get('assigned_to');
      const onBehalfOf = params.get('on_behalf_of');
      const status = params.get('status');
      const categorySlug = params.get('category_slug');
      const requestCollectionId = params.get('request_collection_id');
      const requestTypeSlug = params.get('request_type_slug');
      const limit = parseInt(params.get('limit') ?? '50');
      const offset = parseInt(params.get('offset') ?? '0');

      const filtered = requests.filter(r => {
        if (assignedTo && r.assigned_to !== assignedTo) return false;
        if (onBehalfOf && r.on_behalf_of !== onBehalfOf) return false;
        if (status && r.status !== status) return false;
        if (requestTypeSlug && !hasRequestTypeSlug(r, requestTypeSlug)) {
          return false;
        }
        if (
          requestCollectionId &&
          r.request_collection_id !== requestCollectionId
        ) {
          return false;
        }
        if (categorySlug) {
          // `category_slug` is a slug (e.g. 'applications'). Resolve it to the
          // category UUID, then check whether the request's request_type points
          // to that category.
          const category = requestTypeCategories.find(
            c => c.slug === categorySlug
          );
          if (!category) return false;
          const rt = requestTypes.find(rt => rt.id === r.request_type_id);
          if (rt?.request_type_category_id !== category.id) return false;
        }
        return true;
      });

      const page = limit === 0 ? [] : filtered.slice(offset, offset + limit);

      await delay(200);
      return HttpResponse.json(
        { results: page, total: filtered.length },
        { status: 200 }
      );
    }
  ),
  http.get(
    `*/v1/organizations/:organization_id/requests/:request_id`,
    async ({ params }) => {
      const request = requests.find(r => r.id === params.request_id);

      await delay(200);
      if (request) {
        return HttpResponse.json(request, { status: 200 });
      } else {
        return new HttpResponse(null, { status: 404 });
      }
    }
  ),
  http.patch(
    `*/v1/organizations/:organization_id/requests/:request_id`,
    async ({ params, request }) => {
      const requestIndex = requests.findIndex(r => r.id === params.request_id);
      if (requestIndex >= 0) {
        const body = (await request.json()) as Partial<(typeof requests)[0]>;

        requests[requestIndex] = { ...requests[requestIndex], ...body };
      }

      await delay(500);
      if (requestIndex >= 0) {
        return new HttpResponse(null, { status: 200 });
      } else {
        return new HttpResponse(null, { status: 404 });
      }
    }
  ),
  // Mirrors the backend: only IN_PROGRESS requests can be re-executed, and the
  // updated request is returned.
  http.post(
    `*/v1/organizations/:organization_id/requests/:request_id/execute`,
    async ({ params }) => {
      const requestIndex = requests.findIndex(r => r.id === params.request_id);

      await delay(500);
      if (requestIndex === -1) {
        return new HttpResponse(null, { status: 404 });
      }
      if (requests[requestIndex].status !== RequestStatusEnum.InProgress) {
        return new HttpResponse(null, { status: 409 });
      }

      requests[requestIndex] = {
        ...requests[requestIndex],
        status: RequestStatusEnum.Completed,
        automation_status: AutomationStatusEnum.Success,
        resolved_at: new Date().toISOString(),
      };
      return HttpResponse.json(requests[requestIndex], { status: 200 });
    }
  ),
  http.get(`*/v1/request_types`, async () => {
    await delay(200);
    return HttpResponse.json(
      { total: requestTypes.length, results: requestTypes },
      { status: 200 }
    );
  }),
  http.get(`*/v1/system_requesters`, async () => {
    await delay(200);
    return HttpResponse.json(systemRequesters, { status: 200 });
  }),
  http.get(
    `*/v2/organizations/:organization_id/request_type_category_champions`,
    async ({ request }) => {
      await delay(200);
      const categoryId = new URL(request.url).searchParams.get(
        'request_type_category_id'
      );
      const results = requestTypeCategoryChampionsV2.filter(
        champion =>
          !categoryId || champion.request_type_category_id === categoryId
      );
      return HttpResponse.json(
        { results, total: results.length },
        { status: 200 }
      );
    }
  ),
  http.post(
    `*/v2/organizations/:organization_id/request_type_category_champions`,
    async ({ request }) => {
      await delay(200);
      const body = (await request.json()) as CreateCategoryChampionRequest;
      const champion: RequestTypeCategoryChampionV2 = {
        id: `category-champion-${body.employee_id}`,
        organization_id: 'string',
        request_type_category_id: body.request_type_category_id,
        division_id: body.division_id,
        rank: body.rank ?? 0,
        champion: {
          id: `champion-${body.employee_id}`,
          employee_id: body.employee_id,
          status: EmployeeStatusEnum.Active,
        },
      };
      return HttpResponse.json(champion, { status: 201 });
    }
  ),
  http.delete(
    `*/v2/organizations/:organization_id/request_type_category_champions/:assignment_id`,
    async () => {
      await delay(200);
      return new HttpResponse(null, { status: 204 });
    }
  ),
  http.get(
    `*/v1/organizations/:organization_id/request_type_categories`,
    async () => {
      await delay(200);
      return HttpResponse.json(
        { results: requestTypeCategories, total: requestTypeCategories.length },
        { status: 200 }
      );
    }
  ),
  http.get(
    `*/v1/organizations/:organization_id/request_type_categories/:request_type_category_id`,
    async ({ params }) => {
      const category = requestTypeCategories.find(
        c => c.id === params.request_type_category_id
      );
      await delay(200);
      if (category) {
        return HttpResponse.json(category, { status: 200 });
      }
      // Unknown category id — fall back to the first mock so the cell renders something
      return HttpResponse.json(requestTypeCategories[0], { status: 200 });
    }
  ),
  http.get(`*/v1/request_types/:request_type_id`, async ({ params }) => {
    const requestType = requestTypes.find(
      rt => rt.id === params.request_type_id
    );
    await delay(200);
    if (requestType) {
      return HttpResponse.json(requestType, { status: 200 });
    } else {
      return new HttpResponse(null, { status: 404 });
    }
  }),
  // Powers the Configure Applications flow's expandable rows
  // (useProvisioningRequestTypeSettings) -- only Notion has a schema mocked
  // here since it's the one catalog app mocked as already connected.
  http.get(`*/v1/request_type_settings`, async ({ request }) => {
    const url = new URL(request.url);
    const applicationKey = url.searchParams.get('application_key');

    const results =
      applicationKey === 'notion' ? notionRequestTypeSettings : [];

    await delay(200);
    return HttpResponse.json(
      { results, total: results.length },
      { status: 200 }
    );
  }),
  // Powers the Configure Applications flow's Save button
  // (useUpsertSettingsDefaultMutation) once any provisioning setting has
  // been touched for an automated row.
  http.put(
    `*/v1/organization/:organization_id/application/:application_key/setting-defaults/:request_type_key`,
    async ({ params, request }) => {
      const {
        application_key: applicationKey,
        request_type_key: requestTypeKey,
      } = params;
      const body = (await request.json()) as {
        settings: Record<string, string | string[] | null>;
      };
      settingsDefaultsByAppAndRequestType.set(
        `${applicationKey}:${requestTypeKey}`,
        body.settings
      );

      await delay(300);
      return HttpResponse.json(body.settings, { status: 200 });
    }
  ),
  http.post(
    `*/v1/organizations/:organization_id/requests`,
    async ({ request }) => {
      const body = (await request.json()) as {
        request_type_id: string;
        organization_id: string;
        requester_user_id: string;
        data: Record<string, unknown>;
      };
      const requestData = {
        id: '002df5c0-054f-11ee-be56-0242ac120000',
        request_type_id: body.request_type_id,
        organization_id: body.organization_id,
        requester_user_id: body.requester_user_id,
        status: 'IN_PROGRESS' as RequestDto['status'],
        data: body.data,
        automation_status: 'IN_PROGRESS' as RequestDto['automation_status'],
        on_behalf_of: undefined,
        request_collection_id: undefined,
      };
      requests.push(requestData);
      await delay(500);
      return HttpResponse.json(requestData, { status: 201 });
    }
  ),
  // `is_internal=false` is not honored: every mocked request type is internal,
  // so filtering by it would always return an empty list.
  http.get(
    `*/v1/request_types/:request_type_category_id/request_types`,
    async ({ params }) => {
      const results = requestTypes.filter(
        rt => rt.request_type_category_id === params.request_type_category_id
      );
      await delay(200);
      return HttpResponse.json(
        { results, total: results.length },
        { status: 200 }
      );
    }
  ),
  http.get(
    `*/v1/request_type_settings/:request_type_id/request_type_settings`,
    async ({ params }) => {
      const results = notionRequestTypeSettings.filter(
        rts => rts.request_type_id === params.request_type_id
      );
      await delay(200);
      return HttpResponse.json(
        { results, total: results.length },
        { status: 200 }
      );
    }
  ),
  http.post(
    `*/v1/organizations/:organization_id/add_ons/send_isv_support_request`,
    async () => {
      await delay(200);
      return new HttpResponse(null, { status: 204 });
    }
  ),
  http.post(
    `*/v1/organizations/:organization_id/requests/:request_id/assign`,
    async ({ params, request }) => {
      const requestIndex = requests.findIndex(r => r.id === params.request_id);
      const body = (await request.json()) as { employee_id: string };
      if (requestIndex >= 0) {
        requests[requestIndex] = {
          ...requests[requestIndex],
          assigned_to: body.employee_id,
        };
      }

      await delay(200);
      return new HttpResponse(null, { status: requestIndex >= 0 ? 204 : 404 });
    }
  ),
  http.patch(
    `*/v1/organizations/:organization_id/request_collections/:request_collection_id/requests`,
    async ({ params }) => {
      requests.forEach((r, index) => {
        if (r.request_collection_id === params.request_collection_id) {
          requests[index] = { ...r, status: RequestStatusEnum.Completed };
        }
      });

      await delay(200);
      return new HttpResponse(null, { status: 204 });
    }
  ),
  http.post(
    `*/v1/organizations/:organization_id/group-change/:request_id/resolve`,
    async ({ params }) => {
      const resolved = resolveRequest(params.request_id);
      await delay(200);
      return resolved
        ? HttpResponse.json(resolved, { status: 200 })
        : new HttpResponse(null, { status: 404 });
    }
  ),
  http.post(
    `*/v1/organizations/:organization_id/return-to-work/:request_id/resolve`,
    async ({ params }) => {
      const resolved = resolveRequest(params.request_id);
      await delay(200);
      return resolved
        ? HttpResponse.json(resolved, { status: 200 })
        : new HttpResponse(null, { status: 404 });
    }
  ),
  // Setting-defaults are stored per `applicationKey:requestTypeKey` (group_id
  // is not modeled), see the PUT handler above.
  http.get(
    `*/v1/organization/:organization_id/application/:application_key/setting-defaults`,
    async ({ params }) => {
      const results: RequestTypeSettingDefaultsDto[] = [
        ...settingsDefaultsByAppAndRequestType,
      ]
        .filter(([key]) => key.startsWith(`${params.application_key}:`))
        .map(([key, settings]) => ({
          request_type_slug: key.slice(
            String(params.application_key).length + 1
          ),
          settings,
        }));

      await delay(200);
      return HttpResponse.json(results, { status: 200 });
    }
  ),
  http.get(
    `*/v1/organization/:organization_id/application/:application_key/setting-defaults/:request_type_key/preview`,
    async ({ params }) => {
      const settings =
        settingsDefaultsByAppAndRequestType.get(
          `${params.application_key}:${params.request_type_key}`
        ) ?? {};
      const response: RequestTypeSettingDefaultsDto = {
        request_type_slug: String(params.request_type_key),
        settings,
      };

      await delay(200);
      return HttpResponse.json(response, { status: 200 });
    }
  ),
  http.delete(
    `*/v1/organization/:organization_id/application/:application_key/setting-defaults/:request_type_key`,
    async ({ params }) => {
      settingsDefaultsByAppAndRequestType.delete(
        `${params.application_key}:${params.request_type_key}`
      );

      await delay(200);
      return new HttpResponse(null, { status: 204 });
    }
  ),
];
